import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, noop, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Events } from '../../../utils';
import { CplaceControlComponent } from './cplace-control.component';
import { CplaceControlService } from './cplace-control.service';

@Component({
  template: '',
})
@UntilDestroy()
export abstract class CplaceControlWithEditModeComponent<T> extends CplaceControlComponent<T> implements OnInit {
  /**
   * Submit delay time in milliseconds. This delay is synced with the transitions for the spinner and cancel button
   * inside the control skeleton styles.
   */
  public static readonly SUBMIT_DELAY = 1500;

  /**
   * Value of the cplace control on initialization.
   */
  private $initialValue: BehaviorSubject<T | undefined> = new BehaviorSubject<T | undefined>(undefined);

  /**
   * Whether the cplace control should be shown in edit mode.
   */
  public $editMode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Whether the cplace control should be shown in submit success.
   */
  public $submitSuccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Whether the cplace control should have its submit button disabled.
   */
  public $submitDisabled: Observable<boolean> = combineLatest([this.$value, this.$initialValue]).pipe(
    map(([value, initialValue]) => {
      this._submitDisabled = this.cplaceControlService.deepEqual(value, initialValue);
      return this._submitDisabled;
    })
  );

  private _submitDisabled = true;
  private _explicitCancel = false;
  private _submitProcessTimeout?: number;

  protected constructor(
    public elementRef: ElementRef<HTMLElement>,
    public changeDetectorRef: ChangeDetectorRef,
    public cplaceControlService: CplaceControlService
  ) {
    super(elementRef, changeDetectorRef, cplaceControlService);
    this.$editMode.pipe(untilDestroyed(this)).subscribe((editMode) => this.cplaceControlService.setControlInEditMode(editMode));
  }

  public ngOnInit(): void {
    this.$initialValue.next(this.cplaceControlService.deepClone(this.value));
  }

  /**
   * Called when either the save button or keyboard shortcut is used to save the control.
   */
  public onSubmit() {
    this.$editMode.next(false);
    super.onSubmit();
  }

  /**
   * Called when either the cancel button or keyboard shortcut is used to save the control.
   */
  public onCancel() {
    this.onCancelHook();
    this.submitting = false;
    this._explicitCancel = true;
    this.value = this.cplaceControlService.deepClone(this.$initialValue.getValue());
    this.$editMode.next(false);
  }

  /**
   * Should implement logic that is needed to clear the control data.
   * @protected
   */
  protected abstract onClear(): void;

  protected afterSubmitHook() {
    this.$submitSuccess.next(true);
    this.$initialValue.next(this.cplaceControlService.deepClone(this.value));
  }

  /**
   * Should implement logic that is needed when cancelling out of the control. By default,
   * the value is already reset to the initial value.
   * @protected
   */
  protected onCancelHook(): void {
    noop();
  }

  /**
   * Handles keyboard events for saving, cancelling, and clearing.
   * @param event KeyboardEvent.
   * @private
   */
  @HostListener('window:keydown', ['$event'])
  private onKeyDown(event: KeyboardEvent) {
    if (!this.$editMode.value) {
      return;
    }

    if (Events.isKeyboardSave(event) && !this._submitDisabled) {
      event.preventDefault();
      this.onSubmit();
    }

    if (Events.isKeyboardCancel(event)) {
      event.preventDefault();
      this.onCancel();
    }

    if (Events.isKeyboardClear(event)) {
      event.preventDefault();
      this.onClear();
    }
  }

  /**
   * {@link onFocusIn} and {@link onFocusOut} are both used to control whether the component is in {@link $editMode}.
   * Additionally, they handle submitting the control and the {@link $submitSuccess} state.
   *
   * When focusing into the control, the {@link _submitProcessTimeout} is always cleared in case the submit process
   * needs to be cancelled. {@link $submitSuccess}, {@link submitting}, and {@link _explicitCancel} and are also reset.
   * {@link $editMode} is only entered if the control is not in {@link readonly} or {@link disabled} states.
   *
   * When focusing out of the control, the control content element is retrieved. The submit process is cancelled if the
   * control content element cannot be found, if the control was explicitly cancelled using the cancel button, or if
   * the {@link _submitProcessTimeout} already exists. The submit process is also cancelled if the next focused element
   * is the cancel button or if the next focused element is contained within the control content element. If the submit
   * process is cancelled, then the control will remain in {@link $editMode}. However, if not cancelled, then the submit
   * process is started by first clearing the last clicked element in the {@link cplaceControlService} and enabling the
   * {@link submitting} state.
   *
   * There is a {@link SUBMIT_DELAY} added to give the controls some time in order to regain focus and cancel the submit
   * process. The {@link _submitProcessTimeout} will be cleared when the control focuses in, so the submit process will
   * be cancelled if the control regains focus during the delay period.
   *
   * This is done because some components, notably ones from PrimeNG, take several lifecycles to update themselves
   * before focus is regained. Without the delay, some controls would submit prematurely.
   *
   * When the {@link _submitProcessTimeout} finally runs, the {@link submitting} state is cleared. Then the submit
   * process is cancelled if the document no longer has focus, allowing the user to switch tabs or go out the browser
   * before resuming editing the control. The submit process is also cancelled if the user last clicked on an element
   * contained within the control content.
   *
   * If the control has {@link _submitDisabled} set, meaning the data of the control has not been changed, then {@link
   * onCancel} is run. If the data has changed, then {@link onSubmit} is run, and then the {@link afterSubmitHook} will
   * set the {@link $submitSuccess} state.
   *
   * @private
   */
  @HostListener('focusin')
  private onFocusIn() {
    window.clearTimeout(this._submitProcessTimeout);
    this._submitProcessTimeout = undefined;

    this.$submitSuccess.next(false);
    this.submitting = false;
    this._explicitCancel = false;

    if (!this.readonly && !this.disabled) {
      this.$editMode.next(true);
    }
  }

  /**
   * See {@link onFocusIn}.
   * @param relatedTarget Element that is focused next.
   * @private
   */
  @HostListener('focusout', ['$event.relatedTarget'])
  private onFocusOut(relatedTarget: EventTarget | null) {
    const controlContent: HTMLElement | null = this.elementRef.nativeElement.querySelector(this.createControlContentQuerySelector());

    if (controlContent === null || this._explicitCancel || this._submitProcessTimeout) {
      return;
    }

    if (relatedTarget !== null && relatedTarget instanceof HTMLElement) {
      if (this.isCancelButton(relatedTarget) || controlContent.contains(relatedTarget)) {
        return;
      }
    }

    this.cplaceControlService.resetContainingControlComponentId();
    this.submitting = true;

    this._submitProcessTimeout = window.setTimeout(() => {
      this._submitProcessTimeout = undefined;

      if (document.hasFocus() && !this.cplaceControlService.lastClickedElementContainedWithin(this.componentId)) {
        this._submitDisabled ? this.onCancel() : this.onSubmit();
      } else {
        this.submitting = false;
        this.changeDetectorRef.detectChanges();
      }
    }, CplaceControlWithEditModeComponent.SUBMIT_DELAY);
  }

  /**
   * Creates a query selector for the control's content element.
   * @private
   */
  private createControlContentQuerySelector(): string {
    return `[${CplaceControlService.ATTR_DATA_ELEMENT_NAME}="${CplaceControlService.CONTROL_CONTENT_ELEMENT_NAME}"][${CplaceControlService.ATTR_DATA_COMPONENT_ID}="${this.componentId}"]`;
  }

  /**
   * Checks if the given element is the control's cancel button.
   * @param element Element to check.
   * @private
   */
  private isCancelButton(element: HTMLElement) {
    return (
      element.attributes.getNamedItem(CplaceControlService.ATTR_DATA_ELEMENT_NAME)?.value ===
        CplaceControlService.CANCEL_BUTTON_ELEMENT_NAME &&
      element.attributes.getNamedItem(CplaceControlService.ATTR_DATA_COMPONENT_ID)?.value === this.componentId
    );
  }
}
